import React, { useState, useMemo } from 'react';
import Overlay from '../../components/Overlay';
import ActionButton from '../../components/ActionButton';
import InlineMessage from '../../components/InlineMessage';
import useMessage from '../../lib/useMessage';
import { workOrderRefError, normalizeWorkOrderRef } from '../../lib/workOrderRef';
import { localDate } from '../../lib/format';
import { useTier, controlHeight } from '../../lib/tiers';
import { qtyBox, QTY_BOX_CLASS } from '../../lib/qtyBox';
import {
  resolveFulfilment, chooseSendingBranch, createTransfer,
  SHELF_HORIZONS, MAKE_HORIZONS, leadTimeDays, bestBranchBySurplus,
  proposedAskQty, spareAt, surplusOf,
} from '../../lib/branchTransfers';

// Ask another branch for material. Issues #371 and #425, Session 79.6.
// Gate: docs/gates/Stede_BranchTransfer_Gate_371.html, revision 2.
//
// ── The two-tap rule, which is the whole design constraint ──
//
// Acceptance line T1: a team member at Dallas opens Stock Home, finds a row with
// stock elsewhere, and raises a transfer IN TWO TAPS. CLAUDE.md's own standard is
// every action in two taps under three seconds, and mobile is the priority
// surface.
//
// So this sheet opens ALREADY ANSWERED wherever the app can answer for itself:
//
//   * the item comes from the row that was tapped;
//   * the receiving branch is always the requester's own — never a picker,
//     because you cannot ask for material to be sent somewhere you are not;
//   * the SENDING branch is prefilled with the branch that actually holds the
//     stock, and in MAKE mode it is decided outright and cannot be changed;
//   * the quantity is prefilled with what the row is short by, capped at what
//     the sending branch can spare - #615, Colby's ruling. It WAS the row's
//     reorder_qty, the size you would buy, despite this line saying otherwise.
//
// Everything else — work order, needed-by, a note — is optional. Tap two is the
// send button.
//
// ── Make mode shows no on-hand figure, and that is deliberate ──
//
// Gate D15/D16, AS CORRECTED BY D59 AT SESSION 79.10. Showing "0 at Houston"
// beside a request for 200 stakes would read as a refusal, so the maker's
// on-hand figure stays hidden.
//
// WHAT CHANGED IS THE EXPLANATION, NOT THE RULE. This used to say "nothing is
// on a shelf", which was true when it was written and stopped being true at
// #515: Houston can now record making stock for its own rack and had forty
// frames on it. The sheet no longer claims there is no shelf - it says the
// material will be made OR pulled from their stock, which is true either way
// and reveals nothing.

// #596, Session 79.21b -- the LOCAL day. This was the UTC day
// (`toISOString().slice(0, 10)`), so after about 7pm Central "Today" asked for
// tomorrow and every bracket landed a day late. One helper, in format.js.
function isoDaysFromNow(n) {
  return localDate(n);
}

export default function RequestTransferSheet({
  visible, onClose, onDone, isMobile,
  item,                 // { id, name, sku }
  categories,           // full flat list WITH manufactured_at_branch_id
  branches,             // [{ id, name }]
  elsewhere,            // [{ branchId, qty }] for this item, excluding mine
  myBranchId, myUserId,
  // #514 -- for the approver's email only. Optional: without it the "Requested
  // by" row is omitted rather than rendered empty.
  myUserName,
  // #528 -- stamped onto the transfer so the requester's own department
  // manager can approve it, the same way a material request routes.
  myDepartmentId = null,
  // #615 -- how many the row is SHORT by (null with no reorder point). The
  // sheet turns it into a proposal against whichever branch is sending.
  shortBy,
  // #517 / D53, Session 79.10 -- SEVERAL ITEMS IN ONE ASK.
  //
  // `items` is the multi-item form: [{ id, name, sku, uom, category_id,
  // elsewhere, shortBy }]. When it is absent the sheet behaves EXACTLY as
  // it did for one item, which is the common case and the two-tap one.
  //
  // Almost all of this was already built: branch_transfer_items is a child
  // table, createTransfer() takes an array, and the email renders a many-line
  // table. The single thing making a transfer one item was this sheet.
  items,
  // #684, Session 79.22b.4 -- D14's exception, carried by THIS sheet when a
  // door opens it directly. Stock Home reaches this sheet through its chooser,
  // which shows "Buy from a vendor instead?" itself, so Stock Home passes
  // nothing and is unchanged (Colby left its row as it is). Every other request
  // door now opens this sheet straight away for a made item, and there is no
  // chooser behind it -- so without this line a deliberate purchase of a made
  // item would have no route at all from those doors.
  onBuyInstead,
}) {
  const { message, type, showError, clearMessage } = useMessage();

  // ONE SHAPE FOR BOTH PATHS, so nothing below has to ask which it is.
  const lines = useMemo(() => {
    if (Array.isArray(items) && items.length) {
      return items.map(it => ({ item: it, elsewhere: it.elsewhere || [], shortBy: it.shortBy }));
    }
    return item ? [{ item, elsewhere: elsewhere || [], shortBy }] : [];
  }, [items, item, elsewhere, shortBy]);

  const multi = lines.length > 1;

  // #612, Session 79.22b -- THE CONTROL FLOOR COMES FROM THE POINTER (#417 D6),
  // read from the one place the Make sheet already reads it. This sheet had its
  // own numbers: the superseded 44 on the commit button and no minimum at all
  // on the boxes, while the Make sheet in the same module drew 48. A hook, so
  // it sits here with the others and above the early return.
  const { coarse } = useTier();
  const commitH = controlHeight(coarse, 'commit');
  const minH = controlHeight(coarse, 'min');

  // #615 -- each line opens with its PROPOSAL: what it is short by, capped at
  // what its sending branch can spare. The same branch the rest of the sheet
  // picks - the maker in make mode, otherwise whoever can spare the most - or
  // the one named, when the person has chosen another.
  function proposalFor(l, branchId) {
    const f = resolveFulfilment(categories, l.item?.category_id);
    if (f.mode === 'make') return proposedAskQty({ shortBy: l.shortBy, mode: 'make' });
    const from = branchId || bestBranchBySurplus(l.elsewhere || [])?.branchId;
    return proposedAskQty({ shortBy: l.shortBy, mode: f.mode, spare: from == null ? null : spareAt(l.elsewhere, from) });
  }
  const asText = p => (p.qty != null ? String(p.qty) : '');

  const [qtyMap, setQtyMap] = useState(() =>
    Object.fromEntries(lines.map(l => [String(l.item.id), asText(proposalFor(l))])));
  // Which boxes the person has typed in. A typed number is theirs: choosing a
  // different sending branch re-proposes only the boxes nobody has touched.
  const [typed, setTyped] = useState({});
  const qty = qtyMap[String(item?.id)] ?? '';
  const setQty = v => {
    setTyped(t => ({ ...t, [String(item?.id)]: true }));
    setQtyMap(m => ({ ...m, [String(item?.id)]: v }));
  };
  const [fromBranchId, setFromBranchId] = useState(() => {
    // Prefill with whichever branch has the most TO SPARE — #565 D3, which is
    // #558's rule, imported rather than re-sorted here. This line took the
    // biggest PILE, which is the thing Colby corrected: "if branch a and b both
    // have 100 on hand and a's reorder point is 50 and b's is 75 then branch a
    // has the most to spare." Sorting rather than taking the first still
    // matters — it makes the suggestion the useful one rather than the lowest
    // branch id.
    const best = bestBranchBySurplus(elsewhere || []);
    return best ? String(best.branchId) : '';
  });
  const [neededBy, setNeededBy] = useState('');
  const [workOrder, setWorkOrder] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const fulfilment = useMemo(
    () => resolveFulfilment(categories, item?.category_id),
    [categories, item],
  );

  const choice = useMemo(() => chooseSendingBranch({
    fulfilment,
    requesterBranchId: myBranchId,
    pickedBranchId: fulfilment.mode === 'make' ? null : (fromBranchId ? Number(fromBranchId) : null),
  }), [fulfilment, myBranchId, fromBranchId]);

  const branchName = id => (branches || []).find(b => String(b.id) === String(id))?.name || 'that branch';
  const isMake = fulfilment.mode === 'make';

  // #615 -- THE SHEET SAYS WHAT ITS PROPOSAL IS BUILT FROM, which Colby's
  // ruling requires. Facts rather than the number restated, so the sentence
  // stays true when somebody types their own.
  function askFacts(l, fromId) {
    const p = proposalFor(l, fromId);
    const need = l.shortBy;
    const spare = fromId == null ? null : spareAt(l.elsewhere, fromId);
    const from = branchName(fromId);
    // A branch with NO reorder point counts its whole pile as spare (#558,
    // Colby's ruling), so "without going below its own reorder point" would be
    // false of it. Found on screen: San Antonio, 80 and no floor.
    const floorless = (l.elsewhere || []).some(x => String(x.branchId) === String(fromId) && x.reorderPoint == null);
    // #721, Session 79.22b.5.1b -- WHAT THAT BRANCH HAS ALREADY PROMISED to
    // other transfers is off `spare` already (surplusOf()), and the sentence
    // says so: the approved drawing, and C and D for the cases it did not show.
    // With nothing promised every sentence is exactly what it was.
    const there = (l.elsewhere || []).find(x => String(x.branchId) === String(fromId)) || null;
    const promised = Number(there?.promised) || 0;
    const pile = Number(there?.qty) || 0;
    const promisedNote = promised > 0 ? ` ${promised} of its ${pile} are already promised to other transfers.` : '';
    switch (p.why) {
      case 'capped': return floorless
        ? (promised > 0
          ? `You are short ${need}. ${from} has ${pile}, ${promised} of them already promised, and no reorder point of its own.`
          : `You are short ${need}. ${from} has ${spare}, and no reorder point of its own.`)
        : `You are short ${need}. ${from} can spare ${spare} without going below its own reorder point.${promisedNote}`;
      case 'short': return spare == null ? `You are short ${need}.` : `You are short ${need}. ${from} can spare ${spare}.${promisedNote}`;
      case 'none_to_spare':
        // C -- the promises, not the floor, are what leave nothing: saying "at
        // or below its own reorder point" would be false of a branch above it.
        if (promised > 0 && there && surplusOf({ qty: there.qty, reorderPoint: there.reorderPoint }) > 0) {
          return `You are short ${need}. ${from} has none to spare — ${promised} of its ${pile} are already promised to other transfers. You can still ask.`;
        }
        return `You are short ${need}. ${from} is at or below its own reorder point, so it has none to spare — you can still ask.${promisedNote}`;
      case 'make': return `You are short ${need}.`;
      case 'no_floor': return 'No reorder point is set here, so there is no shortfall to go by.';
      case 'not_short': return 'You are at your reorder point, not below it.';
      default: return null;
    }
  }

  // ── WHO IS SENDING WHAT, when several items were picked at once ──────────
  //
  // A DECISION THE GATE LEFT OPEN, TAKEN HERE AND RECORDED IN IT (D69).
  //
  // Dallas multi-selects three things. Two are made by Houston and one is on
  // Austin's shelf. THAT IS NOT ONE REQUEST -- a transfer is one branch's
  // commitment to send, with one approver and one set of trucks, and two
  // branches cannot share a document. Forcing them into one would mean Houston
  // approving material Austin has to find.
  //
  // So the sheet GROUPS by sending branch and raises one request per group,
  // and says so before anything is sent. That is what purchasing already does
  // with vendors, and what SAP and NetSuite do for the same reason.
  //
  // In practice most selections are one group: every low-stock row at Dallas
  // today resolves to Houston.
  const groups = useMemo(() => {
    const by = new Map();
    for (const l of lines) {
      const f = resolveFulfilment(categories, l.item?.category_id);
      // In make mode the branch is decided. Off a shelf, take whichever branch
      // holds the most -- the one most able to spare it. The single-item path
      // keeps its picker below; a per-item picker on a five-item ask would cost
      // more taps than the whole feature saves.
      // #565 D3, Session 79.15i -- RANKED BY SURPLUS, not by the raw pile, and
      // the rule is imported rather than written here. This line sorted on
      // `qty` while Stock Home sorted on surplus, so the two screens could
      // recommend DIFFERENT branches for the same item. `elsewhere` has carried
      // `reorderPoint` since #558; only this sort never used it.
      const from = f.mode === 'make'
        ? f.branchId
        : (bestBranchBySurplus(l.elsewhere || [])?.branchId ?? null);

      // #565 D9, Session 79.15i -- THE MAKER IS PART OF THE KEY, AND THIS SHIPS
      // IN THE SAME COMMIT AS D1. Named in the gate, in the issue entry and in
      // the ROADMAP row, because a scope sentence naming two things is a
      // checklist and the second one gets dropped.
      //
      // Before D1 the key could not collide: a made item was forced to its
      // maker in `make` mode, so stakes and plywood from San Antonio were
      // already two different documents. D1 lets a made item come off a
      // HOLDER'S shelf, at which point stakes-from-San-Antonio and
      // plywood-from-San-Antonio are BOTH `stock` from the same branch - and
      // they would group together into the first mixed request Stede has ever
      // had.
      //
      // That matters because a transfer carries ONE `made_at_branch_id`. A
      // mixed document would stamp one answer over two different materials, and
      // every rule reading one value per transfer - what it is worth, whether
      // it belongs on a To make tab, where a refusal should route - becomes
      // silently wrong for its second half.
      const madeAt = f.branchId ?? 'none';
      const key = `${f.mode}:${from ?? 'none'}:${madeAt}`;
      if (!by.has(key)) by.set(key, { mode: f.mode, fromBranchId: from, departmentId: f.departmentId, madeAtBranchId: f.branchId ?? null, lines: [] });
      by.get(key).lines.push(l);
    }
    return [...by.values()];
  }, [lines, categories]);

  // WHETHER ANYTHING HERE HAS TO BE MADE, which decides the needed-by options.
  //
  // THIS IS NOT COSMETIC AND IT NEARLY SHIPPED WRONG. `isMake` reads the single
  // `item`, which does not exist on a multi-item ask -- so every multi-item
  // request would have offered Today / Tomorrow / This week, the SHELF
  // horizons. Colby caught exactly that at 79.8 on the single-item sheet:
  // "making 100 frames cant happen today, cant happen tomorrow and could maybe
  // happen this week." One made item in the selection means the whole ask needs
  // manufacturing horizons and a real date, because the slowest line sets the
  // date for all of them.
  const anyMake = multi ? groups.some(g => g.mode === 'make') : isMake;

  // The work order number is checked against the SAME pattern the database
  // enforces, using the shared module #499 added at 79.1 — so the form refuses
  // what Postgres would refuse, rather than accepting it and failing on save.
  // That mismatch is #502, live today on material requests, and it is not being
  // reproduced here.
  const woError = workOrder.trim() ? workOrderRefError(workOrder) : null;

  const qtyNumber = Number(qty);
  const qtyValid = multi
    // Every line must carry a real number. A blank one is not "send them all" --
    // on a multi-item ask that would commit a branch to a quantity nobody typed.
    ? lines.every(l => {
        const v = qtyMap[String(l.item.id)];
        return v !== '' && v != null && Number.isFinite(Number(v)) && Number(v) > 0;
      })
    : (qty !== '' && Number.isFinite(qtyNumber) && qtyNumber > 0);

  // Off a shelf, an item nobody else holds cannot be asked for at all.
  const unsourceable = multi
    ? groups.filter(g => g.mode === 'stock' && g.fromBranchId == null)
    : [];

  // One sentence, in the order a person would hit it, so the button says the
  // FIRST thing to fix rather than everything at once.
  const blockedReason =
    !qtyValid ? (multi ? 'Type how many of each you need.' : 'Enter how many you need.')
    : unsourceable.length ? `No other branch is holding ${unsourceable[0].lines.map(l => l.item.name).join(' or ')} right now. Remove it, or ask purchasing to buy it.`
    : (!multi && choice.reason) ? choice.reason.charAt(0).toUpperCase() + choice.reason.slice(1) + '.'
    : woError ? woError
    : null;

  async function submit() {
    setBusy(true);
    clearMessage();

    const branchNames = Object.fromEntries((branches || []).map(b => [String(b.id), b.name]));
    // #591 / #593, Session 79.21b -- the person, their department and the
    // maker are no longer sent: create_branch_transfer() reads them itself
    // (the login, and the category tree), so none of them can be forgotten -
    // which is exactly what happened to the maker (#593).
    const common = {
      toBranchId: myBranchId,
      requesterName: myUserName || null,
      neededBy: neededBy || null,
      workOrderRef: workOrder.trim() ? normalizeWorkOrderRef(workOrder) : null,
      notes: notes.trim() || null,
      branchNames,
    };

    // ONE REQUEST PER SENDING BRANCH -- D69. For a single item this is exactly
    // one iteration carrying exactly what it carried before.
    //
    // NOT Promise.all, deliberately. createTransfer() takes a request number
    // from a per-branch sequence and writes notifications and an email; running
    // them together would race the sequence and interleave the mail. Two
    // requests are two conversations and they happen in order.
    const made = [];
    for (const g of (multi ? groups : [{
      mode: fulfilment.mode,
      fromBranchId: choice.fromBranchId,
      departmentId: fulfilment.departmentId,
      lines,
    }])) {
      const result = await createTransfer({
        ...common,
        fromBranchId: g.fromBranchId,
        mode: g.mode,
        // #514 -- `name` and `uom` ride along for the approver's EMAIL and are
        // ignored by the insert, which maps its columns explicitly. Sending the
        // item id alone would mean the mail could only ever say "1 item".
        lines: g.lines.map(l => ({
          item_id: l.item.id,
          qty_requested: Number(qtyMap[String(l.item.id)]),
          name: l.item.name,
          uom: l.item.uom,
        })),
      });
      if (result.error) {
        setBusy(false);
        // WHAT ALREADY WENT IS NAMED RATHER THAN SWALLOWED. A second request
        // failing after the first succeeded is not "nothing happened", and
        // telling somebody it failed when Houston has already been asked is how
        // the same material gets requested twice.
        showError(made.length
          ? `${made.length} request${made.length === 1 ? '' : 's'} went through, then this failed: ${result.error.message}`
          : result.error.message);
        return;
      }
      made.push(result.transfer);
    }

    setBusy(false);
    onDone?.(made[0], made);
    onClose?.();
  }

  if (!visible || lines.length === 0) return null;

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} onClose={busy ? undefined : onClose} maxWidth={480}>
      <div style={{ padding: '1.1rem 1.15rem 1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-head)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 16, fontWeight: 700 }}>
              {multi
                ? `Ask for ${lines.length} items`
                : isMake ? `Ask ${branchName(fulfilment.branchId)} to make these` : 'Ask another branch'}
            </div>
            {/* The subtitle names the ONE item on a single-item ask. On a
                multi-item one there is no single name to show and the list
                below is the answer, so it is omitted rather than guessed at.
                `item` is null on that path -- this line read `item.name` and
                threw on the first multi-item open. */}
            {!multi && item && (
              <>
                <div style={{ fontSize: 13, color: '#0F0F0F', marginTop: 4, fontWeight: 600 }}>{item.name}</div>
                {item.sku && <div style={{ fontSize: 11, color: '#7A7A7A' }}>{item.sku}</div>}
              </>
            )}
          </div>
          <button onClick={onClose} disabled={busy}
            aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#5C5C5C', lineHeight: 1, minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        <InlineMessage type={type} message={message} onDismiss={clearMessage} />

        {/* MAKE MODE: the branch is decided, not chosen, and no on-hand figure
            appears — "0 at Houston" beside a request for two hundred would read
            as a refusal rather than as the truth. Gate D15.

            #522, FIXED AT SESSION 79.10 UNDER GATE D59: THE RULE IS RIGHT AND
            THE EXPLANATION WAS FALSE. This said "there is no shelf to check",
            which was true when it was written and stopped being true at #515 —
            Houston can now record making stock for its own rack, and had forty
            frames on it when Colby looked.

            D59 is explicit that only the sentence changes. Hiding the maker's
            on-hand figure stays, for D15/D16's original reason, so the fix is
            not "show the shelf" — it is to stop claiming there isn't one. */}
        {/* SINGLE-ITEM ONLY. With several items the sending branch is worked out
            per item and reported by the grouping notice below, because a picker
            per row on a five-item ask costs more taps than the whole feature
            saves. */}
        {multi ? (
          groups.length === 1 && groups[0].fromBranchId ? (
            <div style={{
              marginTop: 14, background: '#F1FAF6', border: '1px solid #CDEDDF',
              borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#14503B',
            }}>
              <strong>{branchName(groups[0].fromBranchId)}</strong>
              {groups[0].mode === 'make'
                ? ' makes these — they will be made, or pulled from their stock, and sent here.'
                : ' is holding these and will send them here.'}
            </div>
          ) : null
        ) : isMake ? (
          <div style={{
            marginTop: 14, background: '#F1FAF6', border: '1px solid #CDEDDF',
            borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#14503B',
          }}>
            <strong>{branchName(fulfilment.branchId)}</strong> makes these — they will be made,
            or pulled from their stock, and sent here.
          </div>
        ) : (
          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#3A3A3A', display: 'block', marginBottom: 5 }}>
              Which branch is sending?
            </label>
            {(elsewhere || []).length === 0 ? (
              <div style={{ fontSize: 13, color: '#8A2F2F' }}>
                No other branch is holding any of this right now.
              </div>
            ) : (
              <select
                value={fromBranchId}
                onChange={e => {
                  const v = e.target.value;
                  setFromBranchId(v);
                  // #615 -- the proposal follows the branch, unless the
                  // person has typed a number of their own.
                  if (item && !typed[String(item.id)]) {
                    setQtyMap(m => ({ ...m, [String(item.id)]: asText(proposalFor(lines[0], v)) }));
                  }
                }}
                style={{ width: '100%', fontSize: 16, padding: '9px 8px', border: '1px solid #DDD', borderRadius: 8, background: '#fff', minHeight: minH, boxSizing: 'border-box' }}
              >
                {[...elsewhere].sort((a, b) => b.qty - a.qty).map(o => (
                  <option key={o.branchId} value={o.branchId}>
                    {branchName(o.branchId)} — {o.qty} on hand
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* #517 / D53 — SEVERAL ITEMS, ONE ASK. One row per item with its own
            quantity, which is the whole of what made a transfer single-item.

            The name is not truncated. #518 recorded why on the low-stock list
            and it holds harder here: when every row begins "Stake" or "Frame",
            THE SIZE IS THE IDENTITY, so a cut name is the one thing that must
            not be cut. */}
        {multi ? (
          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#3A3A3A', display: 'block', marginBottom: 6 }}>
              How many of each?
            </label>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 0 /* #710 */ }}>
              <tbody>
                {lines.map(l => (
                  <tr key={l.item.id} style={{ borderBottom: '1px solid #F0F0F0' }}>
                    <td style={{ padding: '9px 0', verticalAlign: 'middle' }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0F0F0F', lineHeight: 1.25 }}>
                        {l.item.name}
                      </div>
                      {l.item.uom && (
                        <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 1 }}>{l.item.uom}</div>
                      )}
                      {/* #615 -- what this line's number is built from. */}
                      {(() => {
                        const f = resolveFulfilment(categories, l.item?.category_id);
                        const fromId = f.mode === 'make' ? f.branchId : bestBranchBySurplus(l.elsewhere || [])?.branchId;
                        const facts = askFacts(l, fromId);
                        return facts ? <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 2, lineHeight: 1.35 }}>{facts}</div> : null;
                      })()}
                    </td>
                    <td style={{ padding: '9px 0 9px 12px', textAlign: 'right', width: 116 }}>
                      <input
                        type="number" inputMode="decimal" min="0"
                        value={qtyMap[String(l.item.id)] ?? ''}
                        onChange={e => {
                          setTyped(t => ({ ...t, [String(l.item.id)]: true }));
                          setQtyMap(m => ({ ...m, [String(l.item.id)]: e.target.value }));
                        }}
                        // #708 -- it starts at the proposed quantity (#615), so it
                        // is a count to confirm: the shared confirm-box style.
                        className={QTY_BOX_CLASS}
                        style={qtyBox()}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* D69 — SAID BEFORE ANYTHING IS SENT, never discovered afterwards.
                Two branches cannot share one transfer, so a mixed selection
                becomes two requests. Somebody who presses Send expecting one
                document and gets two has been surprised by their own app. */}
            {groups.length > 1 && (
              <div style={{
                marginTop: 12, background: '#FDF3E3', borderLeft: '3px solid #EF9F27',
                borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#8A5A10', lineHeight: 1.45,
              }}>
                <strong>This will raise {groups.length} separate requests.</strong>{' '}
                {groups.map((g, i) => (
                  <span key={i}>
                    {i > 0 ? '; ' : ''}
                    {g.lines.length} from {g.fromBranchId ? branchName(g.fromBranchId) : 'nobody — see below'}
                  </span>
                ))}
                . Each branch agrees to its own, because one branch cannot commit another.
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#3A3A3A', display: 'block', marginBottom: 5 }}>
              How many?
            </label>
            <input
              type="number" inputMode="decimal" min="0"
              value={qty}
              onChange={e => setQty(e.target.value)}
              /* 16px minimum — anything smaller makes iOS zoom the page on focus.
                 Locked rule 12, and this is a phone-first screen. */
              className={QTY_BOX_CLASS}
              style={qtyBox({ width: '100%', align: 'left' })}
            />
            {(() => {
              const facts = askFacts(lines[0], isMake ? fulfilment.branchId : (fromBranchId || null));
              return facts ? <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 6, lineHeight: 1.4 }}>{facts}</div> : null;
            })()}
          </div>
        )}

        {/* WHEN IT IS NEEDED, and the options DIFFER BY MODE. This shipped as
            Today / Tomorrow / This week for both, which came from the PURCHASE
            request path (#494) and is right there — somebody puts a box in a
            truck.

            Colby caught it inside a minute of watching a real run: "making 100
            frames cant happen today, cant happen tomorrow and could maybe happen
            this week." Every made-to-order request would have said "this week"
            because it was the only option that was not a lie, and a column where
            every row reads the same is a column nobody reads. Worse, it sets a
            date Houston was never going to hit.

            THE GATE ALREADY DREW THE RIGHT THING and the build was narrower —
            its made-to-order sheet shows "Needed by Sep 20", twelve days out. So
            a made request gets manufacturing horizons AND a real date field,
            while a shelf transfer keeps the shipping ones. */}
        <div style={{ marginTop: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#3A3A3A', display: 'block', marginBottom: 6 }}>
            When do you need it? <span style={{ fontWeight: 400, color: '#7A7A7A' }}>optional</span>
          </label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(anyMake ? MAKE_HORIZONS : SHELF_HORIZONS).map(({ label, days }) => {
              const value = isoDaysFromNow(days);
              const on = neededBy === value;
              return (
                <button key={label} onClick={() => setNeededBy(on ? '' : value)}
                  style={{
                    padding: '8px 14px', borderRadius: 999, fontSize: 13, cursor: 'pointer',
                    border: on ? '1px solid #1D9E75' : '1px solid #E4E4E4',
                    background: on ? '#1D9E75' : '#fff', color: on ? '#fff' : '#3A3A3A',
                    fontWeight: on ? 700 : 500, minHeight: 40,
                  }}>{label}</button>
              );
            })}
          </div>

          {/* A real date as well, for made-to-order only. A job with a hard
              install date needs a day, not a bracket — and a shop floor
              sequencing work needs to be able to tell two orders apart. The
              quick buttons stay because typing a date one-handed in a shop is
              still the worse path when a bracket will do. */}
          {anyMake && (
            <div style={{ marginTop: 9, display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
              <input
                type="date"
                value={neededBy}
                onChange={e => setNeededBy(e.target.value)}
                style={{ fontSize: 16, padding: '9px 10px', border: '1px solid #DDD', borderRadius: 8, minHeight: minH, boxSizing: 'border-box' }}
              />
              {neededBy && leadTimeDays(neededBy) != null && (
                <span style={{ fontSize: 12, color: leadTimeDays(neededBy) < 7 ? '#9A5F00' : '#5C5C5C' }}>
                  {leadTimeDays(neededBy) < 0
                    ? `${Math.abs(leadTimeDays(neededBy))} days ago`
                    : leadTimeDays(neededBy) === 0
                      ? 'today'
                      : `${leadTimeDays(neededBy)} days from now`}
                  {leadTimeDays(neededBy) >= 0 && leadTimeDays(neededBy) < 7 && ' — tight for a production run'}
                </span>
              )}
            </div>
          )}
        </div>

        <div style={{ marginTop: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#3A3A3A', display: 'block', marginBottom: 5 }}>
            Work order <span style={{ fontWeight: 400, color: '#7A7A7A' }}>optional</span>
          </label>
          <input
            value={workOrder}
            onChange={e => setWorkOrder(e.target.value)}
            placeholder="794001"
            style={{ width: '100%', fontSize: 16, padding: '9px 10px', border: `1px solid ${woError ? '#E0A0A0' : '#DDD'}`, borderRadius: 8, boxSizing: 'border-box', minHeight: minH }}
          />
          {woError && <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 5 }}>{woError}</div>}
        </div>

        <div style={{ marginTop: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#3A3A3A', display: 'block', marginBottom: 5 }}>
            Anything they should know? <span style={{ fontWeight: 400, color: '#7A7A7A' }}>optional</span>
          </label>
          <textarea
            value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            style={{ width: '100%', fontSize: 16, padding: 9, border: '1px solid #DDD', borderRadius: 8, boxSizing: 'border-box' }}
          />
        </div>

        <div style={{
          display: 'flex', flexDirection: isMobile ? 'column' : 'row-reverse',
          justifyContent: isMobile ? undefined : 'flex-start',
          alignItems: isMobile ? 'stretch' : 'center', gap: 10, marginTop: 20,
        }}>
          <ActionButton
            onClick={submit}
            blockedReason={blockedReason}
            busy={busy}
            busyLabel="Sending…"
            className="btn btn-primary"
            style={isMobile
              ? { width: '100%', justifyContent: 'center', minHeight: commitH }
              : { minWidth: 150, justifyContent: 'center', minHeight: commitH }}
          >
            {multi
              ? `Ask for ${lines.length} items`
              : isMake ? 'Ask them to make it' : 'Ask for it'}
          </ActionButton>
          <button onClick={onClose} disabled={busy}
            className="btn btn-ghost"
            style={isMobile
              ? { width: '100%', justifyContent: 'center', minHeight: minH }
              : { minWidth: 110, justifyContent: 'center', minHeight: minH }}>
            Cancel
          </button>
        </div>

        {/* #684 B -- the sheet's last line, never focused first, and a sentence
            rather than a peer button so it cannot be hit by accident: the same
            words and the same place MakeForShelfSheet uses (550/551 callout 14).
            Single-item make mode only: a shelf ask is not made by anybody, and a
            multi-item ask has no one item to buy instead. */}
        {onBuyInstead && !multi && isMake && (
          <div style={{ borderTop: '1px solid #EFEFEF', marginTop: 16, paddingTop: 10, fontSize: 13, color: '#3A3A3A' }}>
            {branchName(fulfilment.branchId)} makes these.{' '}
            <button type="button" onClick={onBuyInstead} disabled={busy}
              style={{
                background: 'none', border: 'none', padding: '0 2px', minHeight: minH,
                font: 'inherit', color: '#3A3A3A', textDecoration: 'underline',
                textUnderlineOffset: 3, cursor: 'pointer',
              }}>
              Buy from a vendor instead?
            </button>
          </div>
        )}
      </div>
    </Overlay>
  );
}
